import { Injectable, signal, computed, inject, WritableSignal, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, tap, of, map, catchError } from 'rxjs';
import { Auth, signInWithCustomToken } from '@angular/fire/auth';
import { environment as env } from '../../../environments/environment';
import { ApiResponse } from '../models/api-response.model';
import { LOGIN_ROUTE } from '../constants/constants';
import {
  LoginData,
  LoginRequest,
  UpdateProfileRequest,
  ChangePasswordRequest,
  ResetPasswordRequest,
  ForgotPasswordRequest,
  VerifyOtpRequest,
  VerifyOtpResponseData
} from '../../core/models/auth';
import { AppRole } from '../constants/roles';
import { StorageKey } from '../enums/storage-key.enum';
import { StorageService } from './storage.service';
import { HttpService, SKIP_LOADER, SKIP_ERROR } from './http.service';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private router = inject(Router);
  private http = inject(HttpClient);
  private storageService = inject(StorageService);
  private httpService = inject(HttpService);
  private logger = inject(LoggerService);

  // Signal per notificare il ruolo dell'utente
  public userRole: WritableSignal<AppRole | null> = signal<AppRole | null>(this.getRole());
  public isAuthenticated: Signal<boolean> = computed(() => !!this.userRole());

  // Signal per tracciare se la password deve essere aggiornata
  public passwordUpdateRequired = signal<boolean>(false);

  private auth = inject(Auth);
  // LOGIN
  login(body: LoginRequest): Observable<ApiResponse<LoginData>> {
    return this.httpService.post<LoginData>(env.baseUrl + 'common/login', body).pipe(
      tap((res: ApiResponse<LoginData>) => {
        this.setUser(res.data);
      })
    );
  }

  // Verifica sessione esistente (Auto-login tramite cookie)
  checkSession(): Observable<boolean> {
    return this.http
      .get<ApiResponse<LoginData>>(env.baseUrl + 'common/profile', {
        context: new HttpContext().set(SKIP_LOADER, true).set(SKIP_ERROR, true)
      })
      .pipe(
        tap((res: ApiResponse<LoginData>) => {
          if (res.success && res.data) {
            this.setUser(res.data);
          }
        }),
        map((res) => res.success),
        catchError(() => {
          return of(false);
        })
      );
  }

  private setUser(data: LoginData) {
    // Salva i dati base dell'utente (il token è gestito dal browser via cookie)
    this.storageService.setToLocalStorage(StorageKey.User, data);
    // Aggiorna il signal del ruolo
    this.userRole.set(data.role);

    // Login Firebase con Custom Token se presente
    if (data.firebaseCustomToken) {
      try {
        signInWithCustomToken(this.auth, data.firebaseCustomToken);
      } catch (err) {
        this.logger.error('Firebase login error', err);
      }
    }
  }

  // LOGOUT
  logout(): void {
    // Pulizia completa dello storage
    this.storageService.clearStorage();

    // Pulizia della cache HTTP se disponibile
    this.clearHttpCache();

    // Reset dei signal
    this.userRole.set(null);
    this.passwordUpdateRequired.set(false);

    // Navigazione al login
    this.router.navigate([LOGIN_ROUTE]);
  }

  // Metodo per pulire la cache HTTP
  private clearHttpCache(): void {
    if ('caches' in window) {
      caches
        .keys()
        .then((cacheNames) => {
          return Promise.all(
            cacheNames.map((cacheName) => {
              return caches.delete(cacheName);
            })
          );
        })
        .catch(() => {
          // Ignore cache clear error
        });
    }
  }

  getUserProfile(): Observable<ApiResponse<LoginData>> {
    return this.http.get<ApiResponse<LoginData>>(env.baseUrl + `common/profile`);
  }

  updateProfile(request: UpdateProfileRequest): Observable<ApiResponse<LoginData>> {
    return this.http.put<ApiResponse<LoginData>>(env.baseUrl + `common/profile`, request);
  }

  changePassword(request: ChangePasswordRequest): Observable<ApiResponse<null>> {
    return this.httpService.post<null>(env.baseUrl + 'common/update-password', request);
  }

  forgotPassword(request: ForgotPasswordRequest): Observable<ApiResponse<null>> {
    return this.httpService.post<null>(env.baseUrl + 'common/forgot-password', request);
  }

  verifyOtp(request: VerifyOtpRequest): Observable<ApiResponse<VerifyOtpResponseData>> {
    return this.httpService.post<VerifyOtpResponseData>(env.baseUrl + 'otp/verify', request);
  }

  resetPassword(request: ResetPasswordRequest): Observable<ApiResponse<null>> {
    return this.httpService.post<null>(env.baseUrl + 'common/reset-password', request);
  }

  logOutApi(): Observable<ApiResponse<null>> {
    return this.httpService.post<null>(
      env.baseUrl + 'common/logout',
      {},
      { withCredentials: true }
    );
  }

  getUser(): LoginData | null {
    return this.storageService.getFromLocalStorage<LoginData>(StorageKey.User);
  }

  getRole(): AppRole | null {
    const user = this.getUser();
    return user ? user.role : null;
  }
}
